import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Linkedin, ArrowRight, Download } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { COMPANY, REGIONS, SERVICES, CONTACT_INFO, telHref } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Insights delivered monthly.");
      setEmail("");
    } catch { toast.error("Subscription failed. Please try again."); }
    finally { setLoading(false); }
  };

  const top = SERVICES.slice(0, 8);

  return (
    <footer data-testid="site-footer" className="bg-[var(--dm-navy)] text-white">
      <div className="dm-container py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Link to="/" className="inline-flex">
            <Logo tone="light" size={38} />
          </Link>
          <p className="mt-6 text-sm text-white/70 leading-relaxed max-w-sm">{COMPANY.positioning}</p>

          <form onSubmit={subscribe} data-testid="footer-newsletter-form" className="mt-8 flex items-center border border-white/25 max-w-sm">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Your work email" data-testid="footer-newsletter-input"
              className="bg-transparent px-4 py-3 text-sm w-full placeholder:text-white/40 outline-none"
            />
            <button type="submit" disabled={loading} data-testid="footer-newsletter-submit"
              className="px-4 py-3 bg-[var(--dm-emerald)] hover:bg-[var(--dm-emerald-2)] transition-colors">
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div className="lg:col-span-3">
          <div className="dm-overline mb-4 text-white/60">Services</div>
          <ul className="space-y-2 text-sm">
            {top.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="text-white/80 hover:text-[var(--dm-emerald)]">{s.name}</Link>
              </li>
            ))}
            <li className="pt-2"><Link to="/services" className="text-[var(--dm-emerald)] hover:underline">All services →</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <div className="dm-overline mb-4 text-white/60">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="text-white/80 hover:text-[var(--dm-emerald)]">About</Link></li>
            <li><Link to="/case-studies" className="text-white/80 hover:text-[var(--dm-emerald)]">Case Studies</Link></li>
            <li><Link to="/technologies" className="text-white/80 hover:text-[var(--dm-emerald)]">Technologies</Link></li>
            <li><Link to="/industries" className="text-white/80 hover:text-[var(--dm-emerald)]">Industries</Link></li>
            <li><Link to="/careers" className="text-white/80 hover:text-[var(--dm-emerald)]">Careers</Link></li>
            <li><Link to="/blog" className="text-white/80 hover:text-[var(--dm-emerald)]">Blog</Link></li>
            <li><Link to="/contact" className="text-white/80 hover:text-[var(--dm-emerald)]">Contact</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="dm-overline mb-4 text-white/60">Contact</div>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={`mailto:${CONTACT_INFO.email}`} data-testid="footer-email"
                 className="inline-flex items-start gap-2 text-white/85 hover:text-[var(--dm-emerald)] break-all">
                <Mail className="w-3.5 h-3.5 mt-1 shrink-0 text-[var(--dm-emerald)]" />
                <span>{CONTACT_INFO.email}</span>
              </a>
            </li>
            <li>
              <a href={telHref(CONTACT_INFO.phonePrimary)} data-testid="footer-phone-primary"
                 className="inline-flex items-start gap-2 text-white/85 hover:text-[var(--dm-emerald)]">
                <Phone className="w-3.5 h-3.5 mt-1 shrink-0 text-[var(--dm-emerald)]" />
                <span>{CONTACT_INFO.phonePrimary}</span>
              </a>
            </li>
            <li>
              <a href={telHref(CONTACT_INFO.phoneAlternate)} data-testid="footer-phone-alt"
                 className="inline-flex items-start gap-2 text-white/85 hover:text-[var(--dm-emerald)]">
                <Phone className="w-3.5 h-3.5 mt-1 shrink-0 text-[var(--dm-emerald)]" />
                <span>{CONTACT_INFO.phoneAlternate} <span className="text-white/50">(alternate)</span></span>
              </a>
            </li>
          </ul>

          <div className="dm-overline mt-8 mb-3 text-white/60">Regions Served</div>
          <ul className="space-y-1.5 text-sm">
            {REGIONS.slice(0, 6).map((r) => (
              <li key={r} className="flex items-start gap-2 text-white/75">
                <MapPin className="w-3 h-3 mt-1 text-[var(--dm-emerald)]" />
                <span>{r}</span>
              </li>
            ))}
            <li className="text-xs text-white/50 pl-5">+ 12 more regions worldwide</li>
          </ul>

          <a href="#" onClick={(e) => { e.preventDefault(); toast.info("The company profile PDF will be available after production launch."); }} data-testid="footer-download-profile" className="mt-6 flex items-center gap-2 text-sm text-white/80 hover:text-[var(--dm-emerald)]">
            <Download className="w-4 h-4" /> Download company profile
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="dm-container py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} DataMeridian Consulting. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <a href={`mailto:${COMPANY.email}`} aria-label="Email" className="hover:text-white"><Mail className="w-4 h-4" /></a>
            <a href="#" aria-label="LinkedIn" className="hover:text-white"><Linkedin className="w-4 h-4" /></a>
          </div>
          <div className="uppercase tracking-[0.28em] text-center sm:text-right">
            <div>{COMPANY.brandLine}</div>
            <div className="text-white/40 mt-1">{COMPANY.tagline} · {COMPANY.footerTagline}</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
